import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { swalConfig, toastConfig } from '@/config';
import { generateTitle, generateUrl } from '@/features/tools';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { getGenres, createMovie, getMovie } from '@/api';
import Creatable from 'react-select/creatable';
import BackdropMedia from '@/common/BackdropMedia';
import Input from '@/common/Input';
import PosterMedia from '@/common/PosterMedia';
import RichTextArea from '@/common/RichTextArea';
import useRouteStore from '@/hooks/useRouteStore';

const CreateMovie = () => {
  const mySwal = withReactContent(Swal);
  const [created, setCreated] = useState(false);
  const [validated, setValidated] = useState(false);
  const [movie, setMovie] = useState({
    titulo: '',
    titulo_original: '',
    url: '',
    descripcion: '',
  });
  const [genres, setGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const setRoute = useRouteStore((state) => state.setRoute);

  useEffect(() => {
    setRoute([
      { name: 'Inicio', url: '/administracion' },
      { name: 'Películas', url: '/administracion/peliculas' },
      { name: 'Nueva película', url: '/administracion/peliculas/nueva' },
    ]);
  }, []);

  useEffect(() => {
    getGenres().then((res) => {
      setGenres(res.data.map((genre) => ({ value: genre.name, label: genre.name })));
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (selectedGenres.length > 0 && movie.titulo_original && movie.descripcion) {
      setValidated(true);
    } else {
      setValidated(false);
    }
  }, [selectedGenres, movie]);

  const handleCreateMovie = async () => {
    try {
      const res = await mySwal.fire({ ...swalConfig.confirm, text: `Estás creando la película ${movie.titulo_original}` });

      if (res.isConfirmed) {
        const payload = {
          titulo_original: movie.titulo_original,
          titulo: movie.titulo,
          url: movie.url,
          generos: selectedGenres.map((genre) => genre.value),
          descripcion: movie.descripcion,
          publicada: true,
        };

        const { data } = await createMovie(payload);
        setMovie(data);
        setCreated(true);
        toast.success(`Película creada correctamente`, toastConfig);
      }
    } catch (err) {
      toast.error(err.message, toastConfig);
    }
  };

  const refresh = () => {
    getMovie(movie._id).then((res) => {
      setMovie(res.data);
    });
  };

  return (
    <div className='font-montserrat w-full max-h-full'>
      {!loading ? (
        <>
          <h1 className='font-semibold text-xl pb-1 mb-2 border-b-2'>Película: {movie.titulo_original}</h1>
          {!created ? (
            <>
              <div className='flex flex-col justify-between md:flex-row gap-5 min-w-full'>
                {/* <h2 className='font-semibold text-lg'>Información general</h2> */}
                <Input
                  id='titulo_original'
                  value={movie.titulo_original}
                  onChange={(e) =>
                    setMovie({ ...movie, titulo_original: e.target.value, titulo: generateTitle(e.target.value), url: generateUrl(e.target.value) })
                  }
                  label='Título original'
                  className='w-full'
                />
              </div>
              <div className='flex flex-col md:flex-row gap-5 mt-5 lg:mt-5'>
                <Input id='titulo' value={movie.titulo} label='Título' className='w-full md:w-1/2' disabled />
                <Input id='url' value={movie.url} label='Url' className='w-full md:w-1/2' disabled />
              </div>
              <div className='flex flex-col items-center md:flex-row gap-5 mt-5 lg:mt-5 w-full'>
                <label htmlFor='genres' className='font-semibold text-red-1 text-lg'>
                  Géneros
                </label>
                <Creatable
                  id='genres'
                  isMulti
                  options={genres}
                  value={selectedGenres}
                  onChange={(v) => setSelectedGenres(v)}
                  className='w-full relative z-50'
                />
              </div>
              <div className='mt-5'>
                <label htmlFor='description' className='font-semibold text-red-1 text-lg'>
                  Descripción
                </label>
                <RichTextArea value={movie.descripcion} onChange={(v) => setMovie({ ...movie, descripcion: v })} />
              </div>
              <div className='w-full'>
                <button
                  hidden={!validated}
                  onClick={handleCreateMovie}
                  className='bg-red-1 text-white font-semibold text-lg px-5 py-2 rounded mt-5 hover:text-red-1 hover:bg-opacity-5 transition-all'
                >
                  Crear película
                </button>
              </div>
              <hr className='my-5 p-0' />
            </>
          ) : null}

          {created ? (
            <div>
              <h2 className='font-semibold text-xl pb-1 mb-2'>Media</h2>
              <p className='text-lg'>Ahora debes agregar un poster y/o un backdrop.</p>
              <div className='flex gap-5'>
                <a
                  className='text-[#032541] px-2 py-1 rounded border-2 border-[#032541] font-semibold'
                  href={`https://www.themoviedb.org/search?query=${movie.titulo_original}`}
                  target='_blank'
                >
                  <FontAwesomeIcon icon={faExternalLink} />
                  &nbsp;Buscar en TheMovieDb
                </a>
                <a
                  className='text-[#293a36] px-2 py-1 rounded border-2 border-[#293a36] font-semibold'
                  href={`https://thetvdb.com/search?query=${movie.titulo_original}`}
                  target='_blank'
                >
                  <FontAwesomeIcon icon={faExternalLink} />
                  &nbsp;Buscar en TheTVDb
                </a>
              </div>
              <div className='flex flex-wrap items-center gap-3 w-full mt-2'>
                <PosterMedia source={movie} type='movie' refresh={refresh} />
                <BackdropMedia source={movie} type='movie' refresh={refresh} />
              </div>
              <div className='mt-5'>
                <Link
                  to={`/administracion/peliculas/${movie._id}`}
                  className='text-lg text-red-1 px-2 py-1 rounded border-2 border-red-1 font-semibold hover:bg-red-1 hover:text-white transition-all'
                >
                  Terminar creación
                </Link>
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <h1 className='font-semibold text-xl'>
          <span className='loader2'></span>
        </h1>
      )}
    </div>
  );
};

export default CreateMovie;
